import type { MapSourceUploadSession, ShareScope } from '@/lib/mapping-api';

export type MapSourceUploadSourceKind = 'pmtiles' | 'geojson_file';
export type MapSourceUploadProcessingProfile = 'geojson_to_pmtiles';

export interface PersistedMapSourceUploadSnapshot {
  sessionId: string;
  fileName: string;
  fileSize: number;
  fileLastModified: number;
  replaceTargetId?: string | null;
  name: string;
  description?: string | null;
  shareScope: ShareScope;
  sourceKind?: MapSourceUploadSourceKind;
  processingProfile?: MapSourceUploadProcessingProfile;
  layerId?: string | null;
}

const MAP_SOURCE_UPLOAD_STORAGE_KEY = 'app-portal.map-source-upload-session';

export const fingerprintMapSourceUploadFile = (file: File | null | undefined) => {
  if (!file) return '';
  return `${file.name}:${file.size}:${file.lastModified}`;
};

export const loadPersistedMapSourceUploadSnapshot = (): PersistedMapSourceUploadSnapshot | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(MAP_SOURCE_UPLOAD_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedMapSourceUploadSnapshot;
    if (!parsed?.sessionId || !parsed?.fileName || !Number.isFinite(parsed?.fileSize)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const persistMapSourceUploadSnapshot = (snapshot: PersistedMapSourceUploadSnapshot) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(MAP_SOURCE_UPLOAD_STORAGE_KEY, JSON.stringify(snapshot));
};

export const clearPersistedMapSourceUploadSnapshot = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(MAP_SOURCE_UPLOAD_STORAGE_KEY);
};

export const isMapSourceUploadTerminalStatus = (status?: string | null) =>
  status === 'ready' || status === 'cancelled' || status === 'expired' || status === 'failed';

export const isMapSourceUploadAwaitingMultipartFile = (status?: string | null) =>
  status === 'pending_upload' || status === 'uploading';

export const isMapSourceUploadBackgroundProcessing = (status?: string | null) =>
  status === 'queued_processing' ||
  status === 'processing' ||
  status === 'inspecting' ||
  status === 'registering' ||
  status === 'waiting_for_access_points';

export const describeMapSourceUploadSessionPhase = (session: Pick<MapSourceUploadSession, 'status'>) => {
  switch (session.status) {
    case 'queued_processing':
      return 'Queued for runtime build';
    case 'processing':
      return 'Building runtime artifacts';
    case 'inspecting':
      return 'Inspecting generated runtime tiles';
    case 'registering':
      return 'Registering source';
    case 'waiting_for_access_points':
      return 'Waiting for fiber access points';
    case 'ready':
      return 'Upload complete';
    case 'failed':
      return 'Upload failed';
    case 'cancelled':
      return 'Upload cancelled';
    case 'expired':
      return 'Upload expired';
    default:
      return 'Processing';
  }
};

export const describeMapSourceUploadProgressMessage = (
  session: Pick<MapSourceUploadSession, 'status' | 'processing_progress_message'>,
) => session.processing_progress_message?.trim() || describeMapSourceUploadSessionPhase(session);

export const describeMapSourceUploadTerminalError = (
  title: string,
  session: Pick<MapSourceUploadSession, 'status' | 'error_message'>,
) => {
  const normalizedTitle = title.trim() || 'Upload';
  if (session.status === 'failed') {
    return session.error_message?.trim() || `${normalizedTitle} processing failed.`;
  }
  if (session.status === 'cancelled') {
    return `${normalizedTitle} upload was cancelled.`;
  }
  if (session.status === 'expired') {
    return `${normalizedTitle} upload session expired before processing finished.`;
  }
  return `${normalizedTitle} processing failed.`;
};

export const recentMapSourceUploadProcessingLogs = (
  session: Pick<MapSourceUploadSession, 'processing_logs'> | null | undefined,
  limit = 6,
) => {
  const logs = Array.isArray(session?.processing_logs)
    ? session.processing_logs.filter(line => typeof line === 'string' && line.trim().length > 0)
    : [];
  if (limit <= 0 || logs.length <= limit) {
    return logs;
  }
  return logs.slice(logs.length - limit);
};

const phaseBaseProgress = (status?: string | null) => {
  switch (status) {
    case 'pending_upload':
      return 0;
    case 'uploading':
      return 2;
    case 'queued_processing':
      return 8;
    case 'processing':
      return 20;
    case 'inspecting':
      return 92;
    case 'registering':
      return 97;
    case 'ready':
      return 100;
    case 'waiting_for_access_points':
      return 100;
    case 'failed':
    case 'cancelled':
    case 'expired':
      return 100;
    default:
      return 0;
  }
};

const phaseCeilingProgress = (status?: string | null) => {
  switch (status) {
    case 'queued_processing':
      return 15;
    case 'processing':
      return 88;
    case 'inspecting':
      return 96;
    case 'registering':
      return 99;
    case 'waiting_for_access_points':
      return 100;
    case 'ready':
      return 100;
    default:
      return phaseBaseProgress(status);
  }
};

const coerceDate = (value?: string | null) => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const formatMapSourceUploadElapsed = (elapsedMs: number | null | undefined) => {
  if (!elapsedMs || elapsedMs <= 0) return null;
  const totalSeconds = Math.floor(elapsedMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

export const estimateMapSourceUploadProgress = (
  session: Pick<
    MapSourceUploadSession,
    | 'status'
    | 'created_at'
    | 'updated_at'
    | 'processing_started_at'
    | 'processing_progress_percent'
    | 'processing_progress_message'
  >,
  now = Date.now(),
) => {
  const reportedPercent =
    typeof session.processing_progress_percent === 'number' && Number.isFinite(session.processing_progress_percent)
      ? Math.max(0, Math.min(100, Math.round(session.processing_progress_percent)))
      : null;
  const base = phaseBaseProgress(session.status);
  const ceiling = phaseCeilingProgress(session.status);
  let elapsedMs: number | null = null;

  if (isMapSourceUploadBackgroundProcessing(session.status)) {
    const startTs =
      coerceDate(session.processing_started_at) ??
      coerceDate(session.created_at) ??
      coerceDate(session.updated_at);
    if (startTs) {
      elapsedMs = Math.max(0, now - startTs);
    }
  }

  if (reportedPercent !== null && isMapSourceUploadBackgroundProcessing(session.status)) {
    return {
      phaseLabel: describeMapSourceUploadProgressMessage(session),
      percent: reportedPercent,
      indeterminate: false,
      elapsedMs,
      elapsedLabel: formatMapSourceUploadElapsed(elapsedMs),
    };
  }

  if (session.status === 'processing' || session.status === 'queued_processing') {
    return {
      phaseLabel: describeMapSourceUploadProgressMessage(session),
      percent: null,
      indeterminate: true,
      elapsedMs,
      elapsedLabel: formatMapSourceUploadElapsed(elapsedMs),
    };
  }

  let percent = base;
  if (session.status === 'inspecting' || session.status === 'registering') {
    percent = base;
  }

  return {
    phaseLabel: describeMapSourceUploadSessionPhase(session),
    percent,
    indeterminate: false,
    elapsedMs,
    elapsedLabel: formatMapSourceUploadElapsed(elapsedMs),
  };
};

export const uploadMapSourcePart = (
  url: string,
  body: Blob,
  onProgress: (loadedBytes: number) => void,
  signal?: AbortSignal,
): Promise<void> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    const abortHandler = () => xhr.abort();
    if (signal) {
      if (signal.aborted) {
        reject(new DOMException('Upload cancelled', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', abortHandler, { once: true });
    }

    xhr.upload.addEventListener('progress', event => {
      if (event.lengthComputable) {
        onProgress(event.loaded);
      }
    });

    xhr.addEventListener('load', () => {
      signal?.removeEventListener('abort', abortHandler);
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(body.size);
        resolve();
        return;
      }
      reject(new Error(`Part upload failed with status ${xhr.status}`));
    });

    xhr.addEventListener('error', () => {
      signal?.removeEventListener('abort', abortHandler);
      reject(new Error('Network error while uploading part'));
    });

    xhr.addEventListener('abort', () => {
      signal?.removeEventListener('abort', abortHandler);
      reject(new DOMException('Upload cancelled', 'AbortError'));
    });

    xhr.open('PUT', url);
    xhr.send(body);
  });
